import { useCallback, useEffect, useState } from "react"
import { skipToken } from "@reduxjs/toolkit/query/react"
import { useAppDispatch } from "@/store"
import { availabilityApi, planTag, type SlotPlanTarget, useGetSlotPlanJobQuery } from "../availabilityApi"
import { decodeJobStatus, isFinished } from "./editorModel"
import { clearPendingJob, pendingJobKey, readPendingJob, writePendingJob } from "./pendingJob"

/** How often a running job is asked after. Jobs write a handful of Cal schedules, so seconds. */
export const JOB_POLL_MS = 3000

/**
 * The apply job a schedule is waiting on (AB#781): remembered per schedule so a reload or a return to
 * the page resumes it, polled until it finishes, then forgotten — and the plan and the calendar's
 * availability dropped from the cache, since Cal now publishes something else.
 *
 * Polling follows the booking-manage page: the query is skipped with no job, and stopped by taking
 * its interval to zero once the job has finished. It keeps polling while the tab is in the
 * background, because the point is to have the answer when the user comes back.
 */
export function useSlotPlanJob(target: SlotPlanTarget | null, pollMs: number = JOB_POLL_MS) {
  const dispatch = useAppDispatch()
  const key = target ? pendingJobKey(target) : null

  const [jobId, setJobId] = useState<string | null>(() => (target ? readPendingJob(target) : null))
  const [polling, setPolling] = useState(true)

  // A different schedule picked: whatever it was waiting on, if anything.
  useEffect(() => {
    setJobId(target ? readPendingJob(target) : null)
    setPolling(true)
    // `key` stands for `target`, whose identity changes on every render.
  }, [key])

  const query = useGetSlotPlanJobQuery(jobId ?? skipToken, {
    pollingInterval: polling ? pollMs : 0,
    skipPollingIfUnfocused: false,
  })

  // currentData, not data: data still holds the last job while a new one is being read.
  const job = query.currentData && query.currentData.id === jobId ? query.currentData : undefined
  const status = decodeJobStatus(job?.status)
  const finished = isFinished(status)

  useEffect(() => {
    if (!target || !jobId || !finished) return
    setPolling(false)
    clearPendingJob(target)
    dispatch(availabilityApi.util.invalidateTags([planTag(target), "AmsBookings"]))
  }, [finished, jobId, key, dispatch])

  // A job the server no longer knows (or never did): stop asking after it.
  const notFound = (query.error as { status?: number } | undefined)?.status === 404
  useEffect(() => {
    if (!target || !jobId || !notFound) return
    clearPendingJob(target)
    setJobId(null)
  }, [notFound, jobId, key])

  /** Start waiting on a job a save has just answered with. */
  const track = useCallback((id: string) => {
    if (!target) return
    writePendingJob(target, id)
    setJobId(id)
    setPolling(true)
  }, [key])

  /** Put the finished job's result away. */
  const dismiss = useCallback(() => {
    if (target) clearPendingJob(target)
    setJobId(null)
  }, [key])

  return {
    jobId,
    job,
    status,
    /** A job is queued or running: the editor waits for it before another save. */
    isRunning: jobId !== null && !finished && !notFound,
    error: notFound ? undefined : query.error,
    track,
    dismiss,
  }
}
